import { FilterValueField } from "containers/sites-manager/hooks";
import { SELECTED_SIZE_COLUMN_FIELD, SIZE_COLUMN_FIELD } from "helpers/utils/constants";
import { Validator } from "helpers/validator";
import { formatMessage } from "lib/globalize-util";
import { IIdentifierFields, IUPCData, IUPCDataError, ProductList, UPCRuleType } from "../types";
import { PIPE_CHARACTER_REGEX, FILTER_KEY } from "constants/product-collection";
import { getSanitizedSize } from "helpers/utils/upc";

export class ProductIdentifierMatchRule extends Validator<IUPCData[], IUPCDataError> {
    private readonly products: ProductList;
    private readonly identifierFields: IIdentifierFields[];
    private readonly sizeField = SIZE_COLUMN_FIELD.key;

    constructor(products: ProductList, identifierFields: IIdentifierFields[]) {
        super();
        this.products = products;
        this.identifierFields = identifierFields;
    }

    validate(model: IUPCData[], errors: IUPCDataError): IUPCDataError {
        const hasRequiredColumnsError = !!errors[UPCRuleType.RequiredColumnsRule];

        if (hasRequiredColumnsError) {
            return errors;
        }

        const validationErrors = model.reduce((errors, row) => {
            const size = row[this.sizeField]?.value.toString();
            const rowIdentifiers = this.identifierFields.reduce((acc, identifier) => {
                const rowValue = row[identifier.key]?.value;
                if (rowValue) {
                    acc[identifier.key] = rowValue;
                }
                return acc;
            }, {});

            const foundProduct = this.products.find((product) => {
                const fields = product.get("fields");
                if (!fields) {
                    return false;
                }

                const productIdentifiers = this.identifierFields.reduce((acc, identifier) => {
                    const productValue = fields.get(identifier.key);
                    if (productValue) {
                        acc[identifier.key] = productValue.get(FilterValueField.String);
                    }
                    return acc;
                }, {});

                const productIdField = fields.get(FILTER_KEY.ProductID);
                const hasProductId = Boolean(productIdField?.get(FilterValueField.String));

                const selectedSizeField = fields.get(SELECTED_SIZE_COLUMN_FIELD.key);
                const manufacturedSizeField = fields.get(this.sizeField);

                const productSizes = hasProductId
                    ? getSanitizedSize(manufacturedSizeField)
                    : getSanitizedSize(selectedSizeField, PIPE_CHARACTER_REGEX);

                return (
                    Object.keys(rowIdentifiers).length === this.identifierFields.length &&
                    Object.keys(productIdentifiers).length === this.identifierFields.length &&
                    Object.keys(rowIdentifiers).every(
                        (key) =>
                            rowIdentifiers[key].toString().toLowerCase() ===
                            productIdentifiers[key]?.toString().toLowerCase()
                    ) &&
                    productSizes?.includes(size.toLowerCase())
                );
            });

            if (!foundProduct) {
                const productIdentifierFields = Object.values(rowIdentifiers).join("-");
                const productSize = size.toUpperCase();
                const errorMessage = formatMessage("|product| We could not find a match for this |productAndSize|.", {
                    product: `${productIdentifierFields}-${productSize}:`,
                    productAndSize: "product/size",
                });

                if (errors[UPCRuleType.ProductIdentifierMatchRule]) {
                    errors[UPCRuleType.ProductIdentifierMatchRule].push(errorMessage);
                } else {
                    errors[UPCRuleType.ProductIdentifierMatchRule] = [errorMessage];
                }
            }
            return errors;
        }, errors);

        return super.validate(model, validationErrors);
    }
}
